import { Controller } from "@hotwired/stimulus"

// Displays upload progress during file uploads. Works with both:
// - ActiveStorage direct uploads (listens for native events)
// - Encrypted upload controllers (listens for custom events)
export default class extends Controller {
  static targets = ["overlay", "progressBar", "progressText", "fileName"]
  static values = { encryptingText: { type: String, default: "Encrypting" } }

  connect() {
    this.totalFiles = 0
    this.completedFiles = 0
    this.currentFileProgress = 0

    this.eventHandlers = {
      "direct-upload:initialize": this.handleUploadStart.bind(this),
      "direct-upload:progress": this.handleNativeProgress.bind(this),
      "direct-upload:end": this.handleFileComplete.bind(this),
      "upload:start": this.handleEncryptedUploadStart.bind(this),
      "upload:progress": this.handleEncryptedProgress.bind(this),
      "upload:file-complete": this.handleFileComplete.bind(this),
      "upload:complete": this.handleUploadComplete.bind(this),
      "upload:error": this.handleUploadError.bind(this)
    }

    Object.entries(this.eventHandlers).forEach(([eventName, handler]) => {
      this.element.addEventListener(eventName, handler)
    })
  }

  disconnect() {
    Object.entries(this.eventHandlers || {}).forEach(([eventName, handler]) => {
      this.element.removeEventListener(eventName, handler)
    })
    this.eventHandlers = null
  }

  handleUploadStart(event) {
    // ActiveStorage fires this per-file, count total from form
    const form = this.element.closest("form")
    const fileInputs = form?.querySelectorAll("input[type='file']") || []
    let total = 0
    fileInputs.forEach(input => {
      if (input.files) total += input.files.length
    })
    this.totalFiles = total
    this.completedFiles = 0
    this.showOverlay()
    this.updateProgress(0, event.detail?.file?.name)
  }

  handleEncryptedUploadStart(event) {
    this.totalFiles = event.detail?.totalFiles || 1
    this.completedFiles = 0
    this.showOverlay()
    this.updateProgress(0, event.detail?.fileName)
  }

  handleNativeProgress(event) {
    const { progress } = event.detail
    this.currentFileProgress = progress
    this.updateOverallProgress()
  }

  handleEncryptedProgress(event) {
    const { progress, fileName, phase } = event.detail
    this.currentFileProgress = progress

    if (this.hasFileNameTarget && fileName) {
      const phaseText = phase === "encrypting" ? this.encryptingText() : ""
      this.fileNameTarget.textContent = phaseText ? `${phaseText} ${fileName}` : fileName
    }

    this.updateOverallProgress()
  }

  handleFileComplete(event) {
    this.completedFiles++
    this.currentFileProgress = 0
    this.updateOverallProgress()
  }

  handleUploadComplete(event) {
    // Keep showing 100% briefly before form submits
    this.updateProgress(100)
  }

  handleUploadError(event) {
    this.hideOverlay()
  }

  updateOverallProgress() {
    if (this.totalFiles === 0) return

    // Overall progress = completed files + current file progress
    const fileWeight = 100 / this.totalFiles
    const overallProgress = (this.completedFiles * fileWeight) + (this.currentFileProgress * fileWeight / 100)
    this.updateProgress(Math.round(overallProgress))
  }

  updateProgress(percent, fileName = null) {
    if (this.hasProgressBarTarget) {
      this.progressBarTarget.style.width = `${percent}%`
    }
    if (this.hasProgressTextTarget) {
      this.progressTextTarget.textContent = `${percent}%`
    }
    if (this.hasFileNameTarget && fileName) {
      this.fileNameTarget.textContent = fileName
    }
  }

  showOverlay() {
    if (this.hasOverlayTarget) {
      this.overlayTarget.classList.remove("hidden")
    }
  }

  hideOverlay() {
    if (this.hasOverlayTarget) {
      this.overlayTarget.classList.add("hidden")
    }
  }

  encryptingText() {
    return this.encryptingTextValue
  }
}
